import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { UserListResponse, GetUserListParams } from '../models/users.model';
import { ApiResponse } from '@shared/models/api-response.models';
import { withSuppressErrorToast } from '@core/http/api-response.envelope';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope.
 */
@Injectable({ providedIn: 'root' })
export class UserListService {
  private readonly apiUrl = environment.userManagementApiUrl;

  constructor(private readonly http: HttpClient) {}

  getUserList(params: GetUserListParams = {}): Observable<UserListResponse> {
    const {
      page = 1,
      limit = 10,
      excludeSelf = false,
      search = '',
      order = 'DESC',
      orderBy = 'createdAt',
    } = params;

    const httpParams: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    if (excludeSelf !== undefined) {
      httpParams['excludeSelf'] = excludeSelf.toString();
    }
    if (search) httpParams['search'] = search;
    if (orderBy) httpParams['orderBy'] = orderBy;
    if (order) httpParams['order'] = order;

    return this.http.get<UserListResponse>(`${this.apiUrl}/api/users`, {
      params: httpParams,
    });
  }

  freezeUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/${userId}/freeze`,
      {}
    );
  }

  unfreezeUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/${userId}/unfreeze`,
      {}
    );
  }

  blockUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/${userId}/block`,
      {}
    );
  }

  unblockUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/${userId}/unblock`,
      {}
    );
  }

  /**
   * Suppresses the global error toast so the effect/component can decide
   * whether to surface a toast or a dedicated modal (e.g. the
   * `DUPLICATE_PENDING_APPROVAL` flow renders a modal instead of a toast).
   */
  updateUserRole(
    userId: string,
    role: string
  ): Observable<ApiResponse<unknown>> {
    return this.http.patch<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/${userId}`,
      { role },
      { context: withSuppressErrorToast() }
    );
  }

  addToWishlist(data: {
    email: string;
    phone: string;
    role: string;
  }): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/wishlist`,
      data
    );
  }

  updateWishlistUser(
    userId: string,
    data: { email: string; phone: string; role: string }
  ): Observable<ApiResponse<unknown>> {
    return this.http.patch<ApiResponse<unknown>>(
      `${this.apiUrl}/api/user/v1/${userId}/wishlist`,
      data
    );
  }

  /**
   * Suppresses the global error toast so the effect/component can decide
   * whether to surface a toast or a dedicated modal (e.g. the
   * `DUPLICATE_PENDING_APPROVAL` flow renders a modal instead of a toast).
   */
  deleteUser(userId: string): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(
      `${this.apiUrl}/api/users/${userId}`,
      { context: withSuppressErrorToast() }
    );
  }
}
